import { PersonInformation } from "@/types";
import { fieldTypes, SectionHeader } from "@ucl/library";
import { FC } from "react";
import React from "react";
import { FieldComplexV2 } from "./FieldComplexV2";
import { COMPLEX_FIELD_KEYS } from "@/constants/fieldKeys";
import "./ComplexFields.scss";
import { useAddressApiClient } from "@/hooks/useAddressApiClient";
import classNames from "classnames";

export interface PersonInformationComplexValue {
  showHeader?: boolean;
  headerLabel?: string;
  firstNameLabel?: string;
  hiddenFields?: (keyof typeof COMPLEX_FIELD_KEYS.PERSON_INFORMATION)[];
  readOnly?: boolean;
  className?: string;
  fieldsBeforeHomeAddress?: React.ReactNode;
  shouldHideHomeAddress?: boolean;
}

export interface PersonInformationComplexFieldProps
  extends fieldTypes.BaseFieldProps<PersonInformation> {}

export const getFullName = (personInformation: PersonInformation) => {
  return `${personInformation?.firstName || ""} ${personInformation?.middleName || ""} ${personInformation?.lastName || ""}`.trim();
};
export const getFirstLastName = (personInformation: PersonInformation) => {
  return `${personInformation?.firstName || ""} ${personInformation?.lastName || ""}`.trim();
};

export const PersonInformationComplexField: FC<PersonInformationComplexFieldProps> = (props) => {
  const personInformationComplexValue = props.complexFieldValue as PersonInformationComplexValue;
  const hiddenFields = personInformationComplexValue?.hiddenFields || [];
  const readOnly = personInformationComplexValue?.readOnly || props.readOnly;
  const { getAddressesByQuery, getAddressByQuery } = useAddressApiClient();

  return (
    <section
      className={classNames("complex-field-container person-information", personInformationComplexValue?.className)}
    >
      <SectionHeader label={personInformationComplexValue?.headerLabel || props.label} hidden={!personInformationComplexValue?.showHeader} />
      <div className="complex-field-content">
        <FieldComplexV2
          {...props}
          complexFieldKey={COMPLEX_FIELD_KEYS.PERSON_INFORMATION.FIRST_NAME}
          complexFieldProps={{
            label: personInformationComplexValue?.firstNameLabel || "First Name",
            hidden: hiddenFields.includes("FIRST_NAME"),
            disabled: readOnly,
          }}
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={COMPLEX_FIELD_KEYS.PERSON_INFORMATION.MIDDLE_NAME}
          complexFieldProps={{
            hidden: hiddenFields.includes("MIDDLE_NAME"),
            disabled: readOnly,
          }}
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={COMPLEX_FIELD_KEYS.PERSON_INFORMATION.LAST_NAME}
          complexFieldProps={{
            hidden: hiddenFields.includes("LAST_NAME"),
            disabled: readOnly,
          }}
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={COMPLEX_FIELD_KEYS.PERSON_INFORMATION.DATE_OF_BIRTH}
          complexFieldProps={{
            hidden: hiddenFields.includes("DATE_OF_BIRTH"),
            disabled: readOnly,
          }}
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={COMPLEX_FIELD_KEYS.PERSON_INFORMATION.SOCIAL_SECURITY_NUMBER}
          complexFieldProps={{
            hidden: hiddenFields.includes("SOCIAL_SECURITY_NUMBER"),
            disabled: readOnly,
            defaultToSensitiveHidden: false,
          } as fieldTypes.SocialSecurityNumberV2Props}
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={COMPLEX_FIELD_KEYS.PERSON_INFORMATION.PHONE_NUMBER}
          complexFieldProps={{
            hidden: hiddenFields.includes("PHONE_NUMBER"),
            disabled: readOnly,
          }}
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={COMPLEX_FIELD_KEYS.PERSON_INFORMATION.EMAIL_ADDRESS}
          complexFieldProps={{
            hidden: hiddenFields.includes("EMAIL_ADDRESS"),
            disabled: readOnly,
          }}
        />
        {personInformationComplexValue?.fieldsBeforeHomeAddress}
        <FieldComplexV2
          {...props}
          complexFieldKey={COMPLEX_FIELD_KEYS.PERSON_INFORMATION.HOME_ADDRESS}
          complexFieldProps={
            {
              getAddressesByQuery,
              getAddressByQuery,
              hidden: hiddenFields.includes("HOME_ADDRESS") || personInformationComplexValue?.shouldHideHomeAddress,
              disabled: readOnly,
            } as fieldTypes.SmartAddressProps
          }
        />
      </div>
    </section>
  );
};
